import type { Dispatch, SetStateAction } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ERROR_RATE_LABEL, AuthType } from 'constants/constants'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Paper, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { ActionButton, CanselButton, MessageInfo } from 'components/commonparts'
import { Chart, SearchCondition, StackedBarChart } from 'components/parts/Dashboard'
import ShipExclusion from 'components/parts/Dashboard/ShipExclusion/ShipExclusion'
import { useMonthReport } from 'hooks/useMonthReport'
import { useStock } from 'hooks/useStock'
import { useSearchParamStore } from 'store/searchParamStore'
import { isSameResponseResult } from 'types/types'
import { dateToStr24HPad0DayOfWeek, normalizeDate } from 'utils/convertFromLocalDate'
import { getInitCompanyId } from 'utils/getInitParam'
import { getLocalSearchParam } from 'utils/getLocalSearchParam'

import type { GetStockStockGetRequest, MonthReport, ResponseResult, Stock } from 'api-client'
import type { LineCart } from 'components/parts/Dashboard/Chart/Chart'
import type { ShipExclusionHandle } from 'components/parts/Dashboard/ShipExclusion/ShipExclusion'
import type { StackedBarCart } from 'components/parts/Dashboard/StackedBarChart/StackedBarChart'

export interface InitialChartState {
  left: string | number | undefined
  right: string | number | undefined
  refAreaLeft: string | number | undefined
  refAreaRight: string | number | undefined
  top: string | number
  bottom: string | number
  top2: string | number
  bottom2: string | number
}

export const initialChartState: InitialChartState = {
  left: 'dataMin',
  right: 'dataMax',
  refAreaLeft: '',
  refAreaRight: '',
  top: 'dataMax',
  bottom: 'dataMin',
  top2: 'dataMax',
  bottom2: 'dataMin',
}

const SHIP_STACKED_BAR_CHART: StackedBarCart = {
  height: 500,
  xAxis: 'recDateToNumber',
  yAxisLabel: 'ケース',
  bars: [
    { name: '一般出荷先出荷数', dataKey: 'generalCustomerShipQuantity', fill: '#fbc380' },
    { name: 'PSI得意先出荷数', dataKey: 'otherShipQuantity', fill: '#82c785' },
  ],
  lines: [
    {
      name: '出荷実績',
      dataKey: 'shipQuantityRec',
      stroke: 'transparent',
      hide: true,
    },
    {
      name: '出荷予測',
      dataKey: 'shipQuantityExpected',
      stroke: '#ff6b6b',
      type: 'monotone',
      dot: false,
    },
    {
      name: '除外日（出荷数補完値）',
      dataKey: 'shipQuantityInterpolated',
      stroke: '#81bfda',
      type: 'monotone',
      dot: true,
      showLine: false,
    },
  ],
}

const toIntegerDomain = (min: number, max: number): [number, number] => [Math.floor(min), Math.ceil(max)]

const calculateYDomainFromAllData = (
  data: Stock[] | MonthReport[],
  dataKeys: string[]
): (number | string)[] => {
  if (data.length === 0 || dataKeys.length === 0) {
    return [initialChartState.bottom, initialChartState.top]
  }

  let min = Infinity
  let max = -Infinity

  data.forEach((d) => {
    dataKeys.forEach((key) => {
      const value = (d as unknown as Record<string, unknown>)[key]
      if (value != null && typeof value === 'number' && !Number.isNaN(value)) {
        if (value < min) min = value
        if (value > max) max = value
      }
    })
  })

  if (min === Infinity || max === -Infinity) {
    return [initialChartState.bottom, initialChartState.top]
  }

  const range = max - min
  const padding = range > 0 ? range * 0.1 : 1

  return toIntegerDomain(min - padding, max + padding)
}

const getAxisYDomain = (
  from: string | number | undefined,
  to: string | number | undefined,
  data: Stock[] | MonthReport[],
  dataKeys: string[],
  offset = 0
): (number | string)[] => {
  if (from != null && to != null) {
    const fromNum = typeof from === 'string' ? Number(from) : from
    const toNum = typeof to === 'string' ? Number(to) : to

    if (!Number.isNaN(fromNum) && !Number.isNaN(toNum) && typeof fromNum === 'number' && typeof toNum === 'number') {
      const chartData = data as (Stock | MonthReport)[]
      const filteredData = chartData.filter((d) => {
        const xValue = (d as unknown as Record<string, unknown>).recDateToNumber
        if (xValue == null) return false
        const xNum = typeof xValue === 'string' ? Number(xValue) : (xValue as number)
        return !Number.isNaN(xNum) && xNum >= fromNum && xNum <= toNum
      })

      if (filteredData.length === 0) {
        return calculateYDomainFromAllData(data, dataKeys)
      }

      let min = Infinity
      let max = -Infinity

      filteredData.forEach((d) => {
        dataKeys.forEach((key) => {
          const value = (d as unknown as Record<string, unknown>)[key]
          if (value != null && typeof value === 'number' && !Number.isNaN(value)) {
            if (value < min) min = value
            if (value > max) max = value
          }
        })
      })

      if (min === Infinity || max === -Infinity) {
        return calculateYDomainFromAllData(data, dataKeys)
      }

      const range = max - min
      const padding = range > 0 ? range * 0.1 : offset

      return toIntegerDomain(min - padding, max + padding)
    }
  }
  return [initialChartState.bottom, initialChartState.top]
}

function Dashboard() {
  const shipChart: LineCart = {
    height: 500,
    xAxis: 'recDateToNumber',
    yAxisLabel: 'ケース',
    line: [
      {
        chartType: 'SimpleLine',
        name: '出荷実績',
        type: 'monotone',
        dataKey: 'shipQuantityRec',
        stroke: '#f68700',
      },
      {
        chartType: 'SimpleLine',
        name: '出荷予測',
        type: 'monotone',
        dataKey: 'shipQuantityExpected',
        stroke: '#ffcc8f',
      },
      {
        chartType: 'SimpleLine',
        name: '除外日（出荷数補完値）',
        type: 'monotone',
        dataKey: 'shipQuantityInterpolated',
        stroke: '#81bfda',
        dot: true,
      },
      {
        chartType: 'SimpleLine',
        name: ERROR_RATE_LABEL,
        type: 'monotone',
        dataKey: 'errorRate',
        // NOTE: 暫定対応 凡例のiconType="plainline"にすると legendType='none'が効かないため色をwhiteにして擬似的に非表示にする
        stroke: 'white',
        hide: true,
      },
    ],
  }

  const orderChart: LineCart = {
    height: 500,
    xAxis: 'recDateToNumber',
    yAxisLabel: 'ケース',
    line: [
      {
        chartType: 'SimpleLine',
        name: '発注実績',
        type: 'monotone',
        dataKey: 'orderQuantityRec',
        stroke: '#0bb7df',
      },
      {
        chartType: 'SimpleLine',
        name: '発注予測',
        type: 'monotone',
        dataKey: 'orderQuantityRecommend',
        stroke: '#a0e9fa',
      },
    ],
  }

  const stockoutChart: LineCart = {
    height: 500,
    xAxis: 'recDateToNumber',
    yAxisLabel: '日',
    line: [
      {
        chartType: 'SimpleLine',
        name: '欠品率実績',
        type: 'monotone',
        dataKey: 'stockoutRate',
        stroke: '#5cb743',
      },
      // {
      //   chartType: 'SimpleLine',
      //   name: '欠品率予測',
      //   type: 'monotone',
      //   dataKey: 'stockoutExpectedRate',
      //   stroke: '#cae8c2',
      // },
      {
        chartType: 'SimpleLine',
        name: '目標',
        type: 'monotone',
        dashedLine: true,
        dataKey: 'safetyStockSetting',
        stroke: '#ffc000',
        dot: false,
      },
    ],
  }

  const stockDaysChart: LineCart = {
    height: 500,
    xAxis: 'recDateToNumber',
    yAxisLabel: '日',
    showZeroLine: true,
    zeroLineStroke: '#7f7f7f',
    zeroLineStrokeWidth: 0.3,
    line: [
      {
        chartType: 'SimpleLine',
        name: '在庫日数',
        type: 'monotone',
        dataKey: 'stockDays',
        stroke: '#6150ba',
      },
      // {
      //   chartType: 'SimpleLine',
      //   name: '在庫日数予測',
      //   type: 'monotone',
      //   dataKey: 'stockDaysExpected',
      //   stroke: '#bab1e9',
      // },
      {
        chartType: 'SimpleLine',
        name: '目標',
        type: 'monotone',
        dashedLine: true,
        dataKey: 'stockDaysSetting',
        stroke: '#ffc000',
        dot: false,
      },
    ],
  }

  const stockTransitionChart: LineCart = {
    height: 500,
    xAxis: 'recDateToNumber',
    yAxisLabel: 'ケース',
    yAxisRight: '2',
    yAxisRightLabel: '日',
    showZeroLine: true,
    zeroLineStroke: '#7f7f7f',
    zeroLineStrokeWidth: 0.3,
    line: [
      {
        chartType: 'StackedBar',
        name: '在庫数',
        dataKey: 'stockQuantity',
        fill: '#b0a8dd',
        yAxisId: '1',
      },
      {
        chartType: 'SimpleLine',
        name: '安全在庫数',
        type: 'monotone',
        dataKey: 'calcSafety',
        stroke: '#ff6b6b',
        dashedLine: true,
        dot: false,
        yAxisId: '1',
      },
      {
        chartType: 'SimpleLine',
        name: '在庫日数',
        type: 'monotone',
        dataKey: 'stockDays',
        stroke: '#5cb743',
        yAxisId: '2',
        dot: false,
      },
      {
        chartType: 'SimpleLine',
        name: '在庫日数設定',
        type: 'monotone',
        dashedLine: true,
        dataKey: 'stockDaysSetting',
        stroke: '#ffc000',
        dot: false,
        yAxisId: '2',
      },
    ],
  }

  const { loginUserInfo } = useAuthContext()
  const shipExclusionRef = useRef<ShipExclusionHandle>(null)

  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  const { stock, fetchStock, isLoading: isStockLoading, result } = useStock()
  const { monthReport, fetchMonthReport, isLoading: isMonthReportLoading, result: monthReportResult } = useMonthReport()

  const shipStackedBarData = useMemo(
    () =>
      stock.list.map((item) => ({
        ...item,
        otherShipQuantity:
          (item.shipQuantityRec ?? 0) - (item.generalCustomerShipQuantity ?? 0),
        // `shipQuantityInterpolated` は補完値そのものなので、StackedBarの「列の頭」に点を出したい場合は
        // 実績(=列の合計)の位置にプロットするための補助フィールドを作る
      })),
    [stock.list]
  )

  // 自社の検索条件をローカルストレージから取得する
  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)

  const localCompanyId = localSearchParam?.dashboard.companyId || undefined
  const localCenterId = localSearchParam?.dashboard.centerId || undefined
  const localItemCode = localSearchParam?.dashboard.itemCode || ''
  const localDateRange = localSearchParam?.dashboard.dateRange || 'year'

  const { search } = useLocation()
  const query = new URLSearchParams(search)
  // クエリパラメーターを取得
  const trQueryParam = {
    companyId: Number(query.get('company_id')),
    centerId: Number(query.get('center_id')),
    itemCode: query.get('item_code'),
    dateRange: query.get('date_range'),
  }

  const initSearchParam: GetStockStockGetRequest = {
    // ユーザーの企業ID
    companyId: getInitCompanyId(trQueryParam.companyId, localCompanyId, loginUserInfo!.companyId),
    centerId: trQueryParam.centerId ? trQueryParam.centerId : localCenterId,
    itemCode: trQueryParam.itemCode ? trQueryParam.itemCode : localItemCode,
    dateRange: trQueryParam.dateRange ? trQueryParam.dateRange : localDateRange,
  }

  const [isOpen, setIsOpen] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const [searchParam, setSearchParam] = useState<GetStockStockGetRequest>(initSearchParam)
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [shipChartState, setShipChartState] = useState<InitialChartState>(initialChartState)
  const [stockChartState, setStockChartState] = useState<InitialChartState>(initialChartState)
  const [stockoutChartState, setStockoutChartState] = useState<InitialChartState>(initialChartState)
  const [stockDaysChartState, setStockDaysChartState] = useState<InitialChartState>(initialChartState)
  const [stockTransitionChartState, setStockTransitionChartState] = useState<InitialChartState>(initialChartState)
  const [shipStackedBarChartState, setShipStackedBarChartState] = useState<InitialChartState>(initialChartState)

  const setGridMessageIfChanged = useCallback<Dispatch<SetStateAction<ResponseResult | undefined>>>((value) => {
    setGridMessage((prev) => {
      const next = typeof value === 'function' ? value(prev) : value
      return isSameResponseResult(prev, next) ? prev : next
    })
  }, [])

  useEffect(() => {
    if (result) {
      setResultMessage((prev) => (isSameResponseResult(prev, result) ? prev : result))
    }
  }, [result])

  useEffect(() => {
    if (gridMessage) {
      setResultMessage((prev) => (isSameResponseResult(prev, gridMessage) ? prev : gridMessage))
    }
  }, [gridMessage])

  const navigate = useNavigate()
  const onSearch = (requestParam: GetStockStockGetRequest) => {
    // /stock API
    fetchStock({
      ...requestParam,
      viewUnitType: 'day',
      dateRange: 'year',
    })

    // /month-report API
    fetchMonthReport(requestParam)

    setSearchParam({ ...requestParam, viewUnitType: 'day', dateRange: 'year' })

    // 検索条件をローカルストレージに保存する
    if (loginUserInfo?.tenantId && loginUserInfo?.companyId) {
      setSearchParamState<GetStockStockGetRequest>(loginUserInfo?.tenantId, loginUserInfo?.companyId, 'Dashboard', {
        companyId: requestParam.companyId,
        centerId: requestParam.centerId,
        itemCode: requestParam.itemCode,
        viewUnitType: 'day',
        dateRange: 'year',
      })
    }

    // 検索実行時に検索条件をクエリパラメータとして付与する
    navigate(
      `?company_id=${requestParam.companyId}&center_id=${requestParam.centerId}&item_code=${requestParam.itemCode}&date_range=year`
    )  
  }

  const onReload = useCallback(() => {
    setSearchTrigger((prev) => !prev);
  }, [])
  const onChartPlotSelectDate = useCallback(
    (data: Stock | MonthReport) => {
      if (!isOpen) return
      shipExclusionRef.current?.addExclusionFromChart(data as Stock)
    },
    [isOpen]
  )

  const handleRegister = () => {
    setUpdateTrigger((prev) => !prev);
  }
  // if no search condition set open to false
  useEffect(() => {
    if(searchParam.itemCode === '' && searchParam.centerId === undefined) {
      setIsOpen(false)
    }
  }, [searchParam])
  // Cancel Process
  const handleCancel = () => {
    fetchStock(searchParam);
  };

  const stockRecDateFormatter = useCallback((trRecDateToNumber: number) => {
    const newRecDate = new Date(trRecDateToNumber)
    return dateToStr24HPad0DayOfWeek(newRecDate, 'YYYY/MM/DD (WW)', false)
  }, [])

  // 数値に変換されたrecDateをm(月)となるように文字列を返す
  const monthReporRecDateFormatter = (trRecDateToNumber: number) => {
    const newRecDate = new Date(trRecDateToNumber)
    return dateToStr24HPad0DayOfWeek(newRecDate, 'MM月', false)
  }

  const todayLineX = useMemo((): number | null => {
    if (!loginUserInfo?.systemDate) return null
    const systemDate = normalizeDate(loginUserInfo.systemDate)
    if (!systemDate) return null
    systemDate.setHours(0, 0, 0, 0)
    return systemDate.getTime()
  }, [loginUserInfo?.systemDate])

  const chartZoom = useCallback((
    trChartState: InitialChartState,
    setChartState: Dispatch<SetStateAction<InitialChartState>>,
    data: Stock[] | MonthReport[],
    chartDetail: LineCart
  ) => {
    let { refAreaLeft, refAreaRight } = trChartState

    if (refAreaLeft === refAreaRight || refAreaRight === '' || !refAreaLeft || !refAreaRight) {
      return setChartState((state) => ({
        ...state,
        refAreaLeft: '',
        refAreaRight: '',
      }))
    }

    // 文字列を数値に変換して比較
    const leftNum = typeof refAreaLeft === 'string' ? Number(refAreaLeft) : refAreaLeft
    const rightNum = typeof refAreaRight === 'string' ? Number(refAreaRight) : refAreaRight
    
    if (Number.isNaN(leftNum) || Number.isNaN(rightNum)) {
      return setChartState((state) => ({
        ...state,
        refAreaLeft: '',
        refAreaRight: '',
      }))
    }

    if (leftNum > rightNum) {
      [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
    }

    // Y軸1（左側）のdataKeysを取得
    const yAxis1Keys = chartDetail.line
      .filter((line) => (line.yAxisId || '1') === '1' && !line.hide)
      .map((line) => line.dataKey)

    // Y軸2（右側）のdataKeysを取得
    const yAxis2Keys = chartDetail.yAxisRight
      ? chartDetail.line
          .filter((line) => line.yAxisId === chartDetail.yAxisRight && !line.hide)
          .map((line) => line.dataKey)
      : []

    // Y軸のドメインを計算
    const [bottom, top] = getAxisYDomain(refAreaLeft, refAreaRight, data, yAxis1Keys, 1)
    const [bottom2, top2] = yAxis2Keys.length > 0
      ? getAxisYDomain(refAreaLeft, refAreaRight, data, yAxis2Keys, 1)
      : [initialChartState.bottom2, initialChartState.top2]

    return setChartState((state) => ({
      ...state,
      refAreaLeft: '',
      refAreaRight: '',
      left: refAreaLeft,
      right: refAreaRight,
      bottom,
      top,
      bottom2,
      top2,
    }))
  }, [])

  const stackedBarChartZoom = useCallback((
    trChartState: InitialChartState,
    setChartState: Dispatch<SetStateAction<InitialChartState>>,
    data: Stock[] | MonthReport[],
    chartDetail: StackedBarCart
  ) => {
    let { refAreaLeft, refAreaRight } = trChartState

    if (refAreaLeft === refAreaRight || refAreaRight === '' || !refAreaLeft || !refAreaRight) {
      return setChartState((state) => ({
        ...state,
        refAreaLeft: '',
        refAreaRight: '',
      }))
    }

    const leftNum = typeof refAreaLeft === 'string' ? Number(refAreaLeft) : refAreaLeft
    const rightNum = typeof refAreaRight === 'string' ? Number(refAreaRight) : refAreaRight

    if (Number.isNaN(leftNum) || Number.isNaN(rightNum)) {
      return setChartState((state) => ({
        ...state,
        refAreaLeft: '',
        refAreaRight: '',
      }))
    }

    if (leftNum > rightNum) {
      [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
    }

    const barAxis1Keys = chartDetail.bars
      .filter((bar) => (bar.yAxisId || '1') === '1')
      .map((bar) => bar.dataKey)
    const lineAxis1Keys = (chartDetail.lines ?? [])
      .filter((line) => (line.yAxisId || '1') === '1')
      .map((line) => line.dataKey)
    const yAxis1Keys = [...barAxis1Keys, ...lineAxis1Keys]

    const barAxis2Keys = chartDetail.yAxisRight
      ? chartDetail.bars
          .filter((bar) => bar.yAxisId === chartDetail.yAxisRight)
          .map((bar) => bar.dataKey)
      : []
    const lineAxis2Keys = chartDetail.yAxisRight
      ? (chartDetail.lines ?? [])
          .filter((line) => line.yAxisId === chartDetail.yAxisRight)
          .map((line) => line.dataKey)
      : []
    const yAxis2Keys = [...barAxis2Keys, ...lineAxis2Keys]

    const [bottom, top] = getAxisYDomain(refAreaLeft, refAreaRight, data, yAxis1Keys, 1)
    const [bottom2, top2] = yAxis2Keys.length > 0
      ? getAxisYDomain(refAreaLeft, refAreaRight, data, yAxis2Keys, 1)
      : [initialChartState.bottom2, initialChartState.top2]

    return setChartState((state) => ({
      ...state,
      refAreaLeft: '',
      refAreaRight: '',
      left: refAreaLeft,
      right: refAreaRight,
      bottom,
      top,
      bottom2,
      top2,
    }))
  }, [])

  const chartZoomOut = useCallback((setChartState: Dispatch<SetStateAction<InitialChartState>>) => {
    setChartState((state) => ({
      ...state,
      refAreaLeft: '',
      refAreaRight: '',
      left: 'dataMin',
      right: 'dataMax',
      top: 'dataMax',
      bottom: 'dataMin',
      top2: 'dataMax',
      bottom2: 'dataMin',
    }))
  }, [])

  const shipChartPlotHandler = isOpen ? onChartPlotSelectDate : undefined

  useEffect(() => {
    // クエリパラメータまたはローカルストレージに検索条件がある場合は初回レンダリング時に検索を実行する
    if ((trQueryParam.centerId && trQueryParam.itemCode) || (localCenterId && localItemCode)) {
      fetchStock({ ...searchParam, viewUnitType: 'day' })
      fetchMonthReport(searchParam)

      // 検索条件をローカルストレージに保存する
      if (loginUserInfo?.tenantId && loginUserInfo?.companyId) {
        setSearchParamState<GetStockStockGetRequest>(loginUserInfo?.tenantId, loginUserInfo?.companyId, 'Dashboard', {
          ...searchParam,
          viewUnitType: 'day',
        })
      }
    }
  }, [])

  return (
    <Box>
      <Paper variant="outlined"> 
        <SearchCondition searchParam={initSearchParam} onSearch={onSearch} searchTrigger={searchTrigger}/>
      </Paper>
      <Box>
        {/*
          （旧）出荷予測量 ※UAT変更確認用につき、本番リリース時は削除します
          <Typography component="div" variant="largeBold">
            （旧）出荷予測量 ※UAT変更確認用につき、本番リリース時は削除します
          </Typography>
          <Chart
            list={stock.list}
            chartDetail={shipChart}
            chartState={shipChartState}
            setChartState={setShipChartState}
            chartZoom={chartZoom}
            chartZoomOut={chartZoomOut}
            recDateFormatter={stockRecDateFormatter}
            isLoading={isStockLoading}
            // if accordion open -> get Data onPlotSelectDate
            onPlotSelectDate={isOpen ? onPlotSelectDate : undefined}
            clearPlotSelectDate={clearPlotSelectDate}
            todayLineX={todayLineX}
            data={stock.list}
          />
        */}
        <Grid direction="row" container justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
          <Grid component="div">
            <MessageInfo result={resultMessage} />
          </Grid>
        </Grid>
        <Box>
          <Typography component="div" variant="largeBold">
            出荷予測量
          </Typography>
          <StackedBarChart
            list={shipStackedBarData}
            chartDetail={SHIP_STACKED_BAR_CHART}
            chartState={shipStackedBarChartState}
            setChartState={setShipStackedBarChartState}
            chartZoom={stackedBarChartZoom}
            chartZoomOut={chartZoomOut}
            recDateFormatter={stockRecDateFormatter}
            isLoading={isStockLoading}
            onPlotSelectDate={shipChartPlotHandler}
            todayLineX={todayLineX}
            data={shipStackedBarData}
          />
          <Grid direction="row" container justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
            <Grid component="div">
              <MessageInfo result={resultMessage} />
            </Grid>
          </Grid>
        </Box>
        <Accordion   
            expanded={isOpen}
            // set change if accordion open and close
            onChange={() => setIsOpen(prev => !prev)}
            >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
          <Typography variant="largeBold">計算除外日設定</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ShipExclusion
              ref={shipExclusionRef}
              list={stock}
              isLoading={isStockLoading}
              searchParam={searchParam}
              updateTrigger={updateTrigger}
              setHasGridEdited={setHasGridEdited}
              setResultMessage={setGridMessageIfChanged}
              onReload={onReload}
            />
            { loginUserInfo?.authType !== AuthType.ReadOnly && loginUserInfo?.companyId === searchParam.companyId ? (
              <Grid direction="row" container justifyContent="center" sx={{ margin: 1.5  }}>
                <Grid component="div">
                  <ActionButton 
                    onClick={() => { 
                      handleRegister?.(); 
                    }} 
                    disabled={false} 
                  />
                  <CanselButton
                    onClick={() => {handleCancel?.()}}
                    disabled={false}
                  />
                </Grid>
              </Grid>
            ) : (<div/>)}
          </AccordionDetails>
      </Accordion>
      </Box>
      <Box>
        <Typography component="div" variant="largeBold">
          発注推奨量
        </Typography>
        <Chart
          list={stock.list}
          chartDetail={orderChart}
          chartState={stockChartState}
          setChartState={setStockChartState}
          chartZoom={chartZoom}
          chartZoomOut={chartZoomOut}
          recDateFormatter={stockRecDateFormatter}
          isLoading={isStockLoading}
          todayLineX={todayLineX}
          data={stock.list}
        />
        <Grid direction="row" container justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
          <Grid component="div">
            <MessageInfo result={resultMessage} />
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Typography component="div" variant="largeBold">
          在庫推移
        </Typography>
        <Chart
          list={stock.list}
          chartDetail={stockTransitionChart}
          chartState={stockTransitionChartState}
          setChartState={setStockTransitionChartState}
          chartZoom={chartZoom}
          chartZoomOut={chartZoomOut}
          recDateFormatter={stockRecDateFormatter}
          isLoading={isStockLoading}
          todayLineX={todayLineX}
          data={stock.list}
          yAxisRightTickFormatter={(value: number) => (typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(1) : String(value))}
        />
        <Grid direction="row" container justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
          <Grid component="div">
            <MessageInfo result={resultMessage} />
          </Grid>
        </Grid>
      </Box>
          
{/* 
      <Box>
        <Typography component="div" variant="largeBold">
          欠品率
        </Typography>
        <Chart
          list={monthReport.list}
          chartDetail={stockoutChart}
          chartState={stockoutChartState}
          setChartState={setStockoutChartState}
          chartZoom={chartZoom}
          chartZoomOut={chartZoomOut}
          recDateFormatter={monthReporRecDateFormatter}
          isLoading={isMonthReportLoading}
          todayLineX={todayLineX}
          data={monthReport.list}
        />
        <Grid direction="row" container justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
          <Grid component="div">
            <MessageInfo result={monthReportResult} />
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Typography component="div" variant="largeBold">
          在庫日数
        </Typography>
        <Chart
          list={monthReport.list}
          chartDetail={stockDaysChart}
          chartState={stockDaysChartState}
          setChartState={setStockDaysChartState}
          chartZoom={chartZoom}
          chartZoomOut={chartZoomOut}
          recDateFormatter={monthReporRecDateFormatter}
          isLoading={isMonthReportLoading}
          data={monthReport.list}
          yAxisTickFormatter={(value) => (typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(1) : String(value))}
        />
        <Grid direction="row" container justifyContent="space-between">
          <Grid component="div">
            <MessageInfo result={monthReportResult} />
          </Grid>
        </Grid>
      </Box> */}

    </Box>
  )
}
export default Dashboard

/* 

       */
